import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Badge } from '@shared/ui/Badge';
import {
  fmtDate,
  fmtMmAbs,
  fmtTol,
  readString,
  schemeStatusLabel,
  schemeStatusTone,
  sourceFunctionLabel,
  surveyTypePhrase,
  type GeoCheck,
  type GeoDeviationReport,
  type GeoRejectedBy,
  type GeoRequestBrief,
  type GeoSchemeStatus,
} from '@entities/geodesy';
import { statusLabel as requestStatusLabel } from '@entities/requests';
import { Facts, Kpi, KpiGrid, Notice, Num, Stack } from './ui';

export function SchemeStatusBadge({
  status,
  rejectedBy,
}: {
  status: GeoSchemeStatus;
  rejectedBy?: GeoRejectedBy | null;
}) {
  return <Badge $tone={schemeStatusTone(status)}>{schemeStatusLabel(status, rejectedBy)}</Badge>;
}

const CheckList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
`;

const CheckItem = styled.li`
  display: grid;
  grid-template-columns: 92px 1fr;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing(3)};
  font-size: 13.5px;
  line-height: 1.45;
  text-wrap: pretty;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const CheckLevel = styled.span<{ $error: boolean }>`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme, $error }) => ($error ? theme.colors.status.danger : theme.colors.text.secondary)};
`;

/** Контроль ИС по ГОСТ Р 51872 (раздел 7): ошибки блокируют передачу в ПТО. */
export function ChecksList({ checks, only }: { checks: GeoCheck[]; only?: 'error' | 'warning' }) {
  const list = (only ? checks.filter((c) => c.level === only) : checks)
    .slice()
    .sort((a, b) => (a.level === b.level ? 0 : a.level === 'error' ? -1 : 1));
  if (list.length === 0) return null;
  return (
    <CheckList>
      {list.map((c) => (
        <CheckItem key={`${c.code}-${c.message}`}>
          <CheckLevel $error={c.level === 'error'}>
            {c.level === 'error' ? 'Ошибка' : 'Замечание'}
          </CheckLevel>
          <span>{c.message}</span>
        </CheckItem>
      ))}
    </CheckList>
  );
}

/** Итоги сличения: измерено / в допуске / превышено / максимумы. */
export function DeviationTotals({ report }: { report: GeoDeviationReport }) {
  const t = report.totals;
  return (
    <KpiGrid>
      <Kpi value={`${t.measured} из ${t.elements}`} label="измерено элементов" />
      <Kpi value={t.within} label="в допуске" tone={t.within > 0 ? 'success' : undefined} />
      <Kpi
        value={t.exceeded}
        label="с превышением допуска"
        tone={t.exceeded > 0 ? 'danger' : 'success'}
      />
      {t.excluded > 0 && <Kpi value={t.excluded} label="исключено вручную" tone="neutral" />}
      <Kpi
        value={t.max_plan_mm == null ? '—' : `${fmtMmAbs(t.max_plan_mm)} мм`}
        label="макс. в плане"
      />
      <Kpi
        value={t.max_height_mm == null ? '—' : `${fmtMmAbs(t.max_height_mm)} мм`}
        label="макс. по высоте"
      />
    </KpiGrid>
  );
}

const Refs = styled.div`
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ToleranceText = styled.div`
  font-size: 14px;
  line-height: 1.5;
  text-wrap: pretty;
`;

const WarnList = styled.ul`
  margin: 0;
  padding-left: ${({ theme }) => theme.spacing(5)};
`;

/** Предупреждения расчёта отклонений (нет d сваи, точки вне поверхности…). */
export function DeviationWarnings({ report }: { report: GeoDeviationReport }) {
  // Отчёт хранится в JSONB: у рассчитанных раньше поля может не быть.
  const warnings = report.warnings ?? [];
  if (warnings.length === 0) return null;
  return (
    <Notice $tone="warning" role="status">
      <strong>Предупреждения расчёта</strong>
      <WarnList>
        {warnings.map((w) => (
          <li key={w}>{w}</li>
        ))}
      </WarnList>
    </Notice>
  );
}

/** Применённые допуски (справочник по типу работ + переопределения ИС). */
export function AppliedTolerance({ report }: { report: GeoDeviationReport }) {
  const tol = report.tolerance;
  return (
    <Stack $gap={2}>
      <ToleranceText>{tol.description}</ToleranceText>
      <Facts>
        {(tol.plan_along_mm != null || tol.plan_across_mm != null) && (
          <>
            <dt>В плане, мм</dt>
            <dd>
              вдоль <Num>{fmtTol(tol.plan_along_mm)}</Num> · поперёк{' '}
              <Num>{fmtTol(tol.plan_across_mm)}</Num>
            </dd>
          </>
        )}
        {(tol.height_plus_mm != null || tol.height_minus_mm != null) && (
          <>
            <dt>По высоте, мм</dt>
            <dd>
              <Num>{fmtTol(tol.height_plus_mm, tol.height_minus_mm)}</Num>
            </dd>
          </>
        )}
        {tol.vertical_mm != null && (
          <>
            <dt>Вертикальность, мм</dt>
            <dd>
              <Num>{fmtMmAbs(tol.vertical_mm)}</Num>
            </dd>
          </>
        )}
      </Facts>
      {tol.refs.length > 0 && <Refs>{tol.refs.join('; ')}</Refs>}
    </Stack>
  );
}

const BriefLink = styled(Link)`
  display: inline-flex;
  flex-wrap: wrap;
  align-items: baseline;
  column-gap: ${({ theme }) => theme.spacing(2)};
  font-size: 13px;
  font-weight: 600;
`;

const BriefMeta = styled.span`
  font-size: 12.5px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/** Ссылка на заявку, по которой ведётся ИС (вид съёмки, инициатор, срок). */
export function RequestBriefLink({ request }: { request: GeoRequestBrief }) {
  const survey = surveyTypePhrase(readString(request.payload, 'survey_type'));
  const meta = [
    survey ? survey.toLowerCase() : null,
    request.initiator_name ?? sourceFunctionLabel(request.source_function),
    request.due_date ? `срок ${fmtDate(request.due_date)}` : null,
    requestStatusLabel(request.status),
  ].filter(Boolean);
  return (
    <BriefLink to={`/requests/${request.id}`}>
      Заявка «{request.title}»<BriefMeta>{meta.join(' · ')}</BriefMeta>
    </BriefLink>
  );
}
